<template>
  <div
    v-hotkey="shortcuts"
    ref="plannerFooterEl"
    class="planner-footer"
    :class="{ minimized }"
  >
    <div v-if="!minimized" class="drag-div">
      <div
        class="handle handle-tm"
        @mousedown.prevent.stop="startResizingBottomPane"
      />
      <div
        :id="footerDropZoneId"
        ref="dropZoneEl"
        class="wrapper"
        :style="{ height: `${bottomPaneHeight}px` }"
      >
        <BaseButton
          :tabindex="-1"
          class="minimize-button"
          kind="auxiliary"
          :tooltip="{
            text: 'Collapse',
            shortcut: togglePaneActions.bottomPane.shortcutsDisplay,
          }"
          tooltip-display="always"
          @click="minimizePane"
        >
          <template #icon>
            <CollapseIcon />
          </template>
        </BaseButton>
        <div
          v-if="dropZoneEl && days"
          class="list-wrapper"
          :style="{ '--columns': columnCount }"
        >
          <div
            v-for="(date, i) in visibleDates"
            v-show="showTaskList(i)"
            :key="i"
            class="task-list"
          >
            <!--  This Suspense is necessary so that dragzone are setup properly  -->
            <Suspense>
              <FooterTaskList
                ref="taskListEls"
                :height="bottomPaneHeight"
                :date="date"
                :day-index="i"
                :is-loading="isLoading"
                :is-dragging-task="isDraggingTask"
                :is-outside-drop-zone="isOutsideDropZones[i]?.value ?? true"
                :expanded="false"
                :day="getDay(i)"
                :tooltip-position="i > 5 ? 'top-end' : 'top'"
                @unselect-items="$emit('unselectItems')"
                @is-foot-task-list-active="
                  (v) => $emit('isFootTaskListActive', v)
                "
              />
            </Suspense>
          </div>
        </div>
      </div>
    </div>
    <div v-else-if="!isLoading" class="counters">
      <BaseButton
        :tabindex="-1"
        class="minimized-expand-button"
        kind="auxiliary"
        :tooltip="{
          text: 'Expand',
          shortcut: togglePaneActions.bottomPane.shortcutsDisplay,
        }"
        tooltip-display="always"
        @click="toggleFooterList"
      >
        <template #icon>
          <CollapseIcon />
        </template>
      </BaseButton>
      <div class="task-number-wrapper">
        <div
          v-for="(date, i) in visibleDates"
          v-show="showTaskList(i)"
          :key="date.toDateString()"
          class="task-number-item"
        >
          <BaseButton
            v-if="taskCount(i) !== '0'"
            kind="main"
            type="secondary"
            :class="{ isPast: isPastIndex(i) }"
            :text="taskCount(i)"
            true-colors-icon
            @click="minimized = false"
          >
            <template #icon>
              <BaseCheckbox
                checkbox-size="xs"
                :is-checked="isPastIndex(i)"
                :is-interactive="false"
                @click="minimized = false"
              />
            </template>
          </BaseButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { isBefore } from "date-fns/isBefore";
import { isEmpty } from "lodash-es";
import {
  computed,
  defineComponent,
  PropType,
  Ref,
  ref,
  toRefs,
  watch,
} from "vue";

import type { Day } from "@ctrl/controller-rpc";

import { togglePaneActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseCheckbox from "@/components/base/BaseCheckbox.vue";
import CollapseIcon from "@/components/icons/CollapseIcon.vue";
import * as inject from "@/components/inject";
import { getHTMLElement } from "@/components/utils";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useReleaser } from "@/ref";
import { navigateToAgenda } from "@/utils/navigate";

import { UseSettings } from "../settings/controller/settings";
import { assignShortcuts, Shortcuts, toggleBottomPane } from "../shortcuts";
import FooterTaskList from "./PlannerFooterTaskList.vue";
import { footerDropZoneId } from "./types";

export default defineComponent({
  components: {
    BaseButton,
    BaseCheckbox,
    CollapseIcon,
    FooterTaskList,
  },
  props: {
    days: { type: Array as PropType<Day[]>, required: true },
    isDraggingTask: { type: Boolean, required: true },
    isLoading: { type: Boolean, required: true },
    isOutsideDropZones: {
      type: Array as PropType<Ref<boolean>[]>,
      required: true,
    },
    isThreeDayView: { type: Boolean, required: true },
    customDays: { type: Number, default: 3 },
    isWeekendShown: { type: Boolean, required: true },
    weekDates: { type: Array as PropType<Date[]>, required: true },
    localStoragePrefix: { type: String, required: true },
  },
  emits: {
    isFootTaskListActive: (_: boolean) => true,
    setupDropZone: (els: Array<HTMLElement>) => !!els,
    unselectItems: () => true,
  },
  async setup(props, { emit }) {
    const BOTTOM_PANE_DEFAULT_HEIGHT = 200;
    const BOTTOM_PANE_MIN_HEIGHT = 100;
    const MIN_CALENDAR_HEIGHT = 300; // Minimum space to reserve for calendar content
    type FooterState = "collapsed" | "expanded";

    const { days, weekDates, isThreeDayView, customDays, isWeekendShown } =
      toRefs(props);
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const router = inject.router();
    const dropZoneEl = ref<HTMLElement>();
    const plannerFooterEl = ref<HTMLElement>();

    // Load & sync minimized state with the browser's local storage.
    const profile = inject.profile();
    const releaser = useReleaser();
    const { syncState } = useBrowserStore(profile.value.user.accountId);
    const minimizedState = releaser.add(
      syncState<FooterState>(
        `${props.localStoragePrefix}:panes/bottom:state`,
        "collapsed"
      )
    );
    const bottomPaneHeight = releaser.add(
      syncState<number>(
        `${props.localStoragePrefix}:panes/bottom:height`,
        BOTTOM_PANE_DEFAULT_HEIGHT
      )
    );

    const minimized = computed({
      get() {
        return minimizedState.value === "collapsed";
      },
      set(bool: boolean) {
        minimizedState.value = bool ? "collapsed" : "expanded";
      },
    });
    const isPastIndex = (dayIndex: number) =>
      isBefore(weekDates.value[dayIndex], today.value);
    const taskCount = (dayIndex: number): string | undefined => {
      if (isEmpty(days.value) || !days.value[dayIndex]) return undefined;
      const day = days.value[dayIndex];
      return isPastIndex(dayIndex)
        ? day.other.length.toString()
        : day.todo.length.toString();
    };
    const goToDay = (dayIndex: number) => {
      const date = getDate(dayIndex);
      navigateToAgenda(router, date);
    };
    const openPane = () => {
      const maxHeight = getMaxHeight();
      minimized.value = false;
      // Restore saved height, but constrain it to valid bounds
      if (bottomPaneHeight.value < BOTTOM_PANE_MIN_HEIGHT) {
        // If below minimum, use default (but don't exceed max)
        bottomPaneHeight.value = Math.min(
          BOTTOM_PANE_DEFAULT_HEIGHT,
          maxHeight
        );
      } else if (bottomPaneHeight.value > maxHeight) {
        // If above maximum, clamp to max (but don't go below min)
        bottomPaneHeight.value = Math.max(BOTTOM_PANE_MIN_HEIGHT, maxHeight);
      }
    };
    const minimizePane = () => {
      minimized.value = true;
    };
    const toggleFooterList = () => {
      if (minimized.value) {
        openPane();
      } else {
        minimizePane();
      }
    };
    const shortcuts = computed<Shortcuts>(() => {
      return {
        ...assignShortcuts(
          { isModalShown, isInputFocused },
          toggleBottomPane,
          toggleFooterList
        ),
      };
    });
    const taskListEls = ref<Array<InstanceType<typeof FooterTaskList>>>([]);
    const expectedDayCount = computed(() =>
      isThreeDayView.value ? customDays.value : isWeekendShown.value ? 7 : 5
    );
    // `weekDates` mirrors the planner's `dates`, which is refreshed on a
    // debounce. When switching from month view (42 dates) into a week/rolling
    // view the footer becomes visible before `dates` shrinks, so we cap the
    // rendered columns to the expected count. This also keeps the index within
    // the bounds of the fixed-size `isOutsideDropZones` array (7 entries),
    // avoiding an undefined `.value` access that otherwise throws on every
    // render and triggers Vue's "Maximum recursive updates exceeded".
    const visibleDates = computed(() =>
      weekDates.value.slice(0, Math.min(expectedDayCount.value, 7))
    );
    const setDropZone = () => {
      if (
        dropZoneEl.value &&
        taskListEls.value.length === expectedDayCount.value
      )
        emit(
          "setupDropZone",
          taskListEls.value.map((el) => getHTMLElement(el))
        );
    };
    // Required because dropZoneEl unmounts when it's minimized
    watch([dropZoneEl, taskListEls], setDropZone, {
      deep: true,
      immediate: true,
    });
    const unselectTask = () =>
      taskListEls.value?.forEach((el) => el.unselectTask());
    const { isWeekStartOnSunday } = UseSettings.getInstance();
    const showTaskList = (i: number): boolean => {
      if (isThreeDayView.value) return true;
      if (isWeekendShown.value) return true;
      const sunday = isWeekStartOnSunday.value ? 0 : 6;
      const staturday = isWeekStartOnSunday.value ? 6 : 5;
      if (i === sunday || i === staturday) return false;
      else return true;
    };
    const getDay = (i: number): Day => {
      if (days.value[i]) return days.value[i];
      else return { todo: [], other: [] };
    };
    const getDate = (i: number): Date => {
      return weekDates.value[i];
    };
    const today = inject.today();

    // Resize functionality
    let initialMouseY = 0;
    let initialHeight = 0;

    const getMaxHeight = (): number => {
      if (!plannerFooterEl.value) return Infinity;

      // Find the calendar drop zone (the actual calendar content area)
      const calendarDropZone = document.querySelector(".calendar-drop-zone");
      if (!(calendarDropZone instanceof HTMLElement)) {
        // Fallback: use viewport height with estimated calendar start
        const viewportHeight = window.innerHeight;
        const estimatedCalendarStart = 250;
        const availableHeight =
          viewportHeight - estimatedCalendarStart - MIN_CALENDAR_HEIGHT;
        return Math.max(BOTTOM_PANE_MIN_HEIGHT, availableHeight);
      }

      // Get the calendar drop zone's bounding rect (this is the actual calendar content)
      const calendarRect = calendarDropZone.getBoundingClientRect();
      // Get viewport height
      const viewportHeight = window.innerHeight;

      // Calculate max height:
      // - Calendar content starts at calendarRect.top
      // - We want at least MIN_CALENDAR_HEIGHT of calendar content to remain visible
      // - So the calendar content should end at least at: calendarRect.top + MIN_CALENDAR_HEIGHT
      // - Footer is at the bottom, so max footer height = viewportHeight - (calendarRect.top + MIN_CALENDAR_HEIGHT)
      const minCalendarEnd = calendarRect.top + MIN_CALENDAR_HEIGHT;
      const availableHeight = viewportHeight - minCalendarEnd;
      return Math.max(BOTTOM_PANE_MIN_HEIGHT, availableHeight);
    };

    const resize = (event: MouseEvent) => {
      if (minimized.value) return;
      const deltaY = initialMouseY - event.clientY;
      const newHeight = initialHeight + deltaY;
      setHeight(newHeight);
    };

    const setHeight = (value: number) => {
      const maxHeight = getMaxHeight();
      const constrainedHeight = Math.max(
        BOTTOM_PANE_MIN_HEIGHT,
        Math.min(maxHeight, value)
      );
      bottomPaneHeight.value = constrainedHeight;
    };

    const stopResizingBottomPane = () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizingBottomPane);
    };

    const startResizingBottomPane = (event: MouseEvent) => {
      if (minimized.value) return;
      event.preventDefault();
      initialMouseY = event.clientY;
      initialHeight = bottomPaneHeight.value;
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResizingBottomPane);
    };

    return {
      bottomPaneHeight,
      columnCount: expectedDayCount,
      dropZoneEl,
      footerDropZoneId,
      getDay,
      isPastIndex,
      minimized,
      minimizePane,
      openPane,
      goToDay,
      plannerFooterEl,
      shortcuts,
      showTaskList,
      startResizingBottomPane,
      taskCount,
      taskListEls,
      toggleFooterList,
      visibleDates,
      togglePaneActions,
      unselectTask,
    };
  },
});
</script>

<style lang="scss" scoped>
.planner-footer {
  position: relative;
  display: flex;
  width: 100%;
  @include themed() {
    background-color: t($bg);
    border-top: 1px solid t($grey-10);
  }

  .wrapper {
    display: flex;
    flex: 1;
    width: 100%;
    padding-inline-start: var(--planner-axis-width, 49px);
    overflow-y: scroll;
    position: relative;
    height: 100%;
  }

  .drag-div {
    display: flex;
    flex: 1;
  }

  .list-wrapper {
    display: grid;
    grid-auto-rows: unset;
    grid-template-columns: repeat(var(--columns, 5), 1fr);
    grid-row-gap: 0;
    grid-column-gap: 0;
    height: 100%;
    width: 100%;
  }

  &.minimized {
    position: absolute;
    bottom: 0;
    padding-bottom: 12px;
    background-color: transparent;
    border-top: 0;
    min-height: 30px;

    .counters {
      display: flex;
      flex: 1;
    }

    .task-number-wrapper {
      display: flex;
      flex: 1;
      flex-direction: row;
      justify-content: space-around;
      padding-inline-start: var(--planner-axis-width, 49px);
      .task-number-item {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        .base-button {
          z-index: $z-index-timeline;
          border-radius: 10px;
          :deep(.left) {
            column-gap: 4px;
          }
          &.isPast {
            @include themed() {
              color: t($grey-50);
            }
          }
        }
      }
    }
  }
  :deep(button.base-button.minimize-button) {
    padding: 6px 6px;
  }
  .minimize-button {
    display: block;
    position: absolute;
    inset-inline-start: 10px;
    bottom: 16px;
    z-index: $z-index-hover-elements;
    transform: rotate(-90deg);
  }

  button.base-button.minimized-expand-button {
    transform: rotate(90deg);
    position: absolute;
    inset-inline-start: 10px;
    padding: 6px;
    z-index: $z-index-content;
  }

  .task-list {
    overflow-y: hidden;
    // min-width: 140px;
    @include themed() {
      border-inline-end: 1px solid t($grey-10);
      background-color: t($bg);
    }

    &.expanded {
      overflow-y: auto;
    }
  }

  .handle.handle-tm {
    top: -12px;
    inset-inline-start: 0;
    cursor: ns-resize;
    height: 24px;
    position: absolute;
    width: 100%;
    z-index: $z-index-content;
    user-select: none;
  }
}
</style>
